// screens/EventScreen.jsx
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Button, Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useGetEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
} from '../slices/eventApiSlice';
import { useFetchAthletesQuery } from '../slices/athleteApiSlice';

const getActivitySource = (event) => {
  if (event?.importedFromGarmin) return 'Garmin';
  if (event?.importedFromStrava) return 'Strava';
  const labels = {
    manual: 'Manual',
    strava: 'Strava',
    garmin: 'Garmin',
    apple_health: 'Apple Health',
    apple_watch: 'Apple Watch',
    iphone: 'iPhone',
  };
  if (event?.source && labels[event.source]) return labels[event.source];
  return 'Manual';
};

const EventScreen = () => {
  const dispatch = useDispatch();

  // Fetch events and athletes, handle mutations
  const { data: events, error, isLoading } = useGetEventsQuery();
  const { data: athletes } = useFetchAthletesQuery(); // Fetch athletes for selection
  const [createEvent, { isLoading: isAdding }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();

  useEffect(() => {
    if (error) {
      toast.error('Failed to fetch events');
    }
  }, [error]);

  // Handle form submission to add a new event
  const handleAddEvent = async (e) => {
    
    e.preventDefault();
    const form = e.target;
    const name = form.name.value;
    const distance = form.distance.value;
    const startTime = form.startTime.value;
    const endTime = form.endTime.value;
    const athlete = form.athlete.value;
    try {
      await createEvent({ name, distance, startTime, endTime, athlete }).unwrap();
      toast.success('Event added successfully');
      form.reset();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to add event');
    }
  };

  // Handle event update
  const handleUpdateEvent = async (event) => {
    try {
      await updateEvent(event).unwrap();
      toast.success('Event updated successfully');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update event');
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteEvent(id).unwrap();
        toast.success('Event deleted successfully');
      } catch (error) {
        toast.error(error?.data?.message || 'Failed to delete event');
      }
    }
  };

  return (
    <div>
      <h1>Events</h1>

      {/* Add Event Form */}
      <Form onSubmit={handleAddEvent}>
        <Row>
          <Col md={2}>
            <Form.Group controlId="name">
              <Form.Label>Event Name</Form.Label>
              <Form.Control type="text" placeholder="Enter event name" required />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="distance">
              <Form.Label>Distance</Form.Label>
              <Form.Control type="text" placeholder="Enter distance" required />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="startTime">
              <Form.Label>Start Time</Form.Label>
              <Form.Control type="datetime-local" required />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="endTime">
              <Form.Label>End Time</Form.Label>
              <Form.Control type="datetime-local" required />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="athlete">
              <Form.Label>Athlete</Form.Label>
              <Form.Control as="select" required>
                <option value="">Select Athlete</option>
                {athletes?.map((athlete) => (
                  <option key={athlete._id} value={athlete._id}>
                    {athlete.firstName} {athlete.lastName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={1} className="d-flex align-items-end">
            <Button type="submit" variant="primary" disabled={isAdding}>
              {isAdding ? 'Adding...' : 'Add Event'}
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Display Events */}
      {isLoading ? (
        <p>Loading events...</p>
      ) : (
        <Table striped bordered hover responsive className="table-sm mt-4">          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Distance</th>
              <th>Points</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Athlete</th>
              <th>Source</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events?.map((event) => (
              <tr key={event._id}>
                <td>{event._id}</td>
                <td>
                  <Form.Control
                    type="text"
                    defaultValue={event.name}
                    onBlur={(e) =>
                      handleUpdateEvent({ ...event, name: e.target.value })
                    }
                    disabled={isUpdating}
                  />
                </td>                <td>
                  <Form.Control
                    type="text"
                    defaultValue={event.distance}
                    onBlur={(e) =>
                      handleUpdateEvent({ ...event, distance: e.target.value })
                    }
                    disabled={isUpdating}
                  />
                </td>
                <td>
                  <span className="text-success fw-bold">
                    {event.points ? event.points.toFixed(1) : ((event.distance || 0) / 1609.34).toFixed(1)}
                  </span>
                </td>
                <td>{new Date(event.startTime).toLocaleString()}</td>
                <td>{new Date(event.endTime).toLocaleString()}</td>
                <td>
                  {event.athlete ? `${event.athlete.firstName} ${event.athlete.lastName}` : 'No athlete assigned'}
                </td>
                <td>{getActivitySource(event)}</td>
                <td>
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteEvent(event._id)}
                    disabled={isDeleting}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default EventScreen;
